import { useParams } from 'react-router-dom';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTaskLogs } from '@/service/gpu-instance/hook';
import { LogEntry, TaskStage, StageStatus } from '@/service/gpu-instance/type';
import {
  Loader2,
  RefreshCw,
  Download,
  Search,
  ChevronDown,
  ScrollText,
  Check,
  Circle,
  XCircle,
} from 'lucide-react';

// Convert nanosecond timestamp to readable format
const formatTimestamp = (nanoseconds: string): string => {
  try {
    const ms = BigInt(nanoseconds) / BigInt(1_000_000);
    const date = new Date(Number(ms));
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return 'Invalid timestamp';
  }
};

// Get log level color based on message content
const getLogLevelColor = (message: string): string => {
  if (message.includes('[ERROR]')) return 'text-red-400';
  if (message.includes('[WARN]')) return 'text-yellow-400';
  if (message.includes('[DEBUG]')) return 'text-gray-400';
  if (message.includes('[INFO]')) return 'text-green-400';
  return 'text-gray-300';
};

// Stage display names
const stageLabels: Record<TaskStage['stage'], string> = {
  docker_pull: 'Docker Pull',
  hf_download: 'HF Download',
  vllm_server: 'vLLM Server',
};

// Get status icon and colors for a stage
const getStageStatusIcon = (status: StageStatus) => {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'running':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'pending':
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
};

// Get stage detail text
const getStageDetail = (stage: TaskStage): string => {
  switch (stage.stage) {
    case 'docker_pull':
      if (stage.elapsed_seconds) {
        return `${stage.elapsed_seconds.toFixed(1)}s`;
      }
      return stage.image || '';
    case 'hf_download':
      if (stage.total_size_human) {
        return stage.total_size_human;
      }
      return stage.model_id || '';
    case 'vllm_server':
      if (stage.api_ready) {
        return 'API Ready';
      }
      return stage.served_model || '';
    default:
      return '';
  }
};

// Get stage border/background color based on status
const getStageContainerClass = (status: StageStatus): string => {
  switch (status) {
    case 'completed':
      return 'border-green-500/50 bg-green-500/10';
    case 'running':
      return 'border-blue-500/50 bg-blue-500/10';
    case 'failed':
      return 'border-red-500/50 bg-red-500/10';
    case 'pending':
    default:
      return 'border-gray-500/30 bg-gray-500/5';
  }
};

// Stages progress component
const StagesProgress = ({ stages }: { stages: TaskStage[] }) => {
  if (!stages || stages.length === 0) return null;

  // Sort stages in expected order
  const stageOrder: TaskStage['stage'][] = ['docker_pull', 'hf_download', 'vllm_server'];
  const sortedStages = [...stages].sort(
    (a, b) => stageOrder.indexOf(a.stage) - stageOrder.indexOf(b.stage)
  );

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Stages</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          {sortedStages.map((stage, index) => (
            <div key={stage.stage} className="flex items-center">
              {/* Stage box */}
              <div
                className={`flex flex-col gap-1 px-4 py-3 rounded-lg border ${getStageContainerClass(stage.status)}`}
              >
                <div className="flex items-center gap-2">
                  {getStageStatusIcon(stage.status)}
                  <span className="font-medium text-sm">
                    {stageLabels[stage.stage]}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground pl-6">
                  {getStageDetail(stage)}
                </span>
              </div>
              {/* Connector line */}
              {index < sortedStages.length - 1 && (
                <div className="w-6 h-px bg-border mx-1" />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const GPUInstanceLogs = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [allLogs, setAllLogs] = useState<LogEntry[]>([]);
  const [oldestTimestamp, setOldestTimestamp] = useState<string | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: logsData,
    isLoading,
    isFetching,
    refetch,
  } = useTaskLogs(instanceId || '', {
    limit: 100,
    enabled: !!instanceId,
    refetchInterval: autoRefresh ? 5000 : false,
  });

  // Update logs when new data arrives
  useEffect(() => {
    if (logsData?.logs && !isLoadingMore) {
      setAllLogs(logsData.logs);
      if (logsData.logs.length > 0) {
        // Get the oldest timestamp from the current batch
        const sorted = [...logsData.logs].sort((a, b) =>
          BigInt(a.timestamp) < BigInt(b.timestamp) ? -1 : 1
        );
        setOldestTimestamp(sorted[0].timestamp);
      }
    }
  }, [logsData, isLoadingMore]);

  // Load more logs (older logs)
  const handleLoadMore = async () => {
    if (!instanceId || !oldestTimestamp) return;

    setIsLoadingMore(true);
    try {
      // Fetch older logs using the start parameter
      const response = await fetch(
        `/task/logs/${instanceId}?limit=100&start=${oldestTimestamp}`
      );
      // In a real implementation, we would use the API function
      // For now, we'll just simulate loading more by using the mock data
      // The actual implementation would merge the new logs with existing ones
      setIsLoadingMore(false);
    } catch (error) {
      console.error('Failed to load more logs:', error);
      setIsLoadingMore(false);
    }
  };

  // Filter logs based on search query
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return allLogs;
    const query = searchQuery.toLowerCase();
    return allLogs.filter((log) =>
      log.message.toLowerCase().includes(query)
    );
  }, [allLogs, searchQuery]);

  // Download logs as text file
  const handleDownload = () => {
    const content = filteredLogs
      .map((log) => `${formatTimestamp(log.timestamp)} ${log.message}`)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${instanceId}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="container py-8 flex justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="h-6 w-6" />
            Server Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            View real-time logs from your GPU instance
          </p>
        </div>
      </div>

      {/* Stages Progress */}
      {logsData?.stages && <StagesProgress stages={logsData.stages} />}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Log Output</CardTitle>
              <CardDescription>
                {logsData?.total
                  ? `Showing ${filteredLogs.length} of ${logsData.total} logs`
                  : 'Loading logs...'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>

              {/* Auto-refresh toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="auto-refresh" className="text-sm">
                  Auto-refresh
                </Label>
              </div>

              {/* Manual refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>

              {/* Download */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={filteredLogs.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredLogs.length === 0 ? (
            <Alert>
              <AlertDescription>
                {searchQuery
                  ? 'No logs match your search query.'
                  : 'No logs available for this instance.'}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Terminal-like log container */}
              <div
                ref={logsContainerRef}
                className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-auto max-h-[600px] min-h-[400px]"
              >
                {filteredLogs.map((log, index) => (
                  <div
                    key={`${log.timestamp}-${index}`}
                    className="flex gap-4 py-1 hover:bg-gray-800/50 px-2 -mx-2 rounded"
                  >
                    <span className="text-gray-500 whitespace-nowrap shrink-0">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <span className={getLogLevelColor(log.message)}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>

              {/* Load more button */}
              {logsData && logsData.total > filteredLogs.length && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ChevronDown className="h-4 w-4 mr-2" />
                    )}
                    Load older logs
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Status indicator */}
      {autoRefresh && (
        <div className="mt-4 flex items-center justify-end text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            Auto-refreshing every 5 seconds
          </div>
        </div>
      )}
    </div>
  );
};

export default GPUInstanceLogs;
